/**
 * 중앙 크롭 + 축소 계획 (순수 함수 — `cropImage.ts` 가 canvas 에 적용한다).
 *
 * - `aspect` 비율로 중앙을 잘라내고(가로가 길면 좌우, 세로가 길면 상하),
 * - `maxSide` 가 있으면 긴 변이 그 값을 넘지 않게 같은 비율로 축소한다(확대는 하지 않는다).
 *
 * 카메라 스트림이 커지면(4:3 최대 해상도 요청) 변환 입력이 3024×4032 까지 갈 수 있어, Lambda 요청 본문
 * 6MB 한도와 Gemini 입력 크기를 상한으로 묶는다 — Gemini 결과는 활성 프리셋 px(기본 1350×1800) 로
 * 생성되므로 그 이상의 입력은 이득이 거의 없다.
 */
export interface CropPlan {
  /** 원본에서 잘라낼 영역 */
  sx: number;
  sy: number;
  sw: number;
  sh: number;
  /** 결과 캔버스 크기 */
  dw: number;
  dh: number;
  /** 잘라낼 것도 줄일 것도 없음 — 재인코딩을 건너뛰어도 된다 */
  identity: boolean;
}

export function planCrop(
  srcW: number,
  srcH: number,
  aspect: { w: number; h: number },
  maxSide?: number
): CropPlan {
  const targetRatio = aspect.w / aspect.h;
  const srcRatio = srcW / srcH;

  let sw = srcW;
  let sh = srcH;
  if (srcRatio > targetRatio) {
    // 가로가 더 긺 → 좌우 잘라냄
    sw = Math.round(srcH * targetRatio);
  } else if (srcRatio < targetRatio) {
    // 세로가 더 긺 → 상하 잘라냄
    sh = Math.round(srcW / targetRatio);
  }
  const sx = Math.round((srcW - sw) / 2);
  const sy = Math.round((srcH - sh) / 2);

  let dw = sw;
  let dh = sh;
  if (maxSide && maxSide > 0) {
    const longest = Math.max(sw, sh);
    if (longest > maxSide) {
      const scale = maxSide / longest;
      dw = Math.max(1, Math.round(sw * scale));
      dh = Math.max(1, Math.round(sh * scale));
    }
  }

  const identity = sw === srcW && sh === srcH && dw === sw && dh === sh;
  return { sx, sy, sw, sh, dw, dh, identity };
}
